import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { SendProps, SendWallet } from 'send/Send'
import { ContactSelector } from 'components/Contacts/ContactSelector'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import { createMFELazy } from 'utils/createMFELazy'

const SendLazy = createMFELazy<SendProps>(() => import('send/Send'), {
  mfeName: 'Send',
  testId: 'send-mfe-error',
})

export const SendMFE = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const context = useWalletActionContext()
  const walletAction = context?.[0] ?? null
  const setWalletAction = context?.[1]
  const sourceWallet = walletAction?.meta?.sourceWallet as SendWallet | undefined

  const { accountCapabilities } = useAccountsContext()
  const isEpcEnabled = accountCapabilities?.isEpcWalletPayoutsCapabilityEnabled
  const { getSupportedCurrencyWallets } = useSupportedCurrencies()

  const [walletSearchTerm, setWalletSearchTerm] = useState('')

  const { data: walletsData, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: walletSearchTerm,
    max: 100,
    order: 'asc',
    sort: 'description',
    includeEmbedded: isEpcEnabled,
  })

  const handleWalletSearch = useCallback((searchTerm: string) => {
    setWalletSearchTerm(searchTerm)
  }, [])

  const { approvalsIsRequiredForFlow: approvalsRequired } = useApprovalsConfigForFlow(
    Approval.FLOWS.CRYPTO_PAYOUT
  )

  const handleCloseOrComplete = () => {
    walletAction?.onClose?.()
  }

  const handleHeaderChange = (header: string) =>
    setWalletAction?.(prev => (prev ? { ...prev, header } : null))

  const handleBackChange = (onBack: (() => void) | undefined) =>
    setWalletAction?.(prev => (prev ? { ...prev, onBack } : null))

  const handleNavigateToPayments = () => navigate(`${PathNames.PAYMENTS}${PathNames.CRYPTO}`)

  const handleOpenConvert = () => {
    setWalletAction?.({
      actionId: ActionIds.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        walletAction?.onClose?.()
      },
    })
  }

  const filteredWallets = getSupportedCurrencyWallets(walletsData?.data ?? []) as SendWallet[]
  const wallets =
    sourceWallet && !filteredWallets.some(w => w.lsid === sourceWallet.lsid)
      ? [sourceWallet, ...filteredWallets]
      : filteredWallets

  const sendProps: SendProps = {
    wallets,
    sourceWallet,
    approvalsRequired: approvalsRequired ?? false,
    isLoadingWallets: isFetchingWallets,
    onOpenConvert: handleOpenConvert,
    onHeaderChange: handleHeaderChange,
    onBackChange: handleBackChange,
    onClose: handleCloseOrComplete,
    onComplete: handleCloseOrComplete,
    onWalletSearch: handleWalletSearch,
    onNavigateToPayments: handleNavigateToPayments,
    onPayoutSuccess: walletAction?.meta?.onPayoutSuccess,
    ContactSelector,
  }

  return <SendLazy {...sendProps} />
}